import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Typography, 
  DatePicker, 
  Space, 
  Button, 
  Table, 
  message, 
  Select,
  Switch,
  Modal,
  Input,
  Divider,
  Tag,
  Alert
} from 'antd';
import { 
  DollarOutlined, 
  ShoppingCartOutlined, 
  CheckCircleOutlined, 
  ReloadOutlined,
  CalculatorOutlined,
  ThunderboltOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { orderService } from '../../services/orderService';
import { 
  OrderStatisticsData, 
  OrderStatisticsQueryRequest,
  OrderStatisticsRealtimeRequest,
  OrderStatisticsRecalculateRequest,
  Page
} from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const OrderStatistics: React.FC = () => {
  const { user } = useAuth();
  
  // 判断是否是管理员（根据实际的角色判断逻辑调整）
  const isAdmin = user?.roles?.includes('ADMIN') || user?.roles?.includes('admin');
  
  // 基础状态
  const [loading, setLoading] = useState(false);
  const [realtimeLoading, setRealtimeLoading] = useState(false);
  const [recalculateLoading, setRecalculateLoading] = useState(false);
  
  // 历史统计数据
  const [statisticsList, setStatisticsList] = useState<OrderStatisticsData[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 31,
    total: 0,
  });
  
  // 实时统计数据
  const [realtimeData, setRealtimeData] = useState<OrderStatisticsData[]>([]);
  
  // 查询条件
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs().subtract(1, 'day'), // 默认查询到昨天
  ]);
  const [selectedDealerId, setSelectedDealerId] = useState<number | undefined>(
    isAdmin ? undefined : user?.dealerId
  );
  const [showGlobalOnly, setShowGlobalOnly] = useState(false);
  
  // 汇总统计
  const [summaryStats, setSummaryStats] = useState({
    totalOrders: 0,
    totalPaidOrders: 0,
    totalAmount: 0,
    avgPaymentRate: 0,
  });

  // 加载历史统计数据
  const loadStatistics = useCallback(async (page: number = 1) => {
    setLoading(true);
    try {
      const params: OrderStatisticsQueryRequest = {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        pageNo: page,
        pageSize: pagination.pageSize,
      };

      // 根据条件设置参数
      if (showGlobalOnly && isAdmin) {
        params.globalOnly = true;
      } else if (selectedDealerId) {
        params.dealerId = selectedDealerId;
      }

      const response = await orderService.queryStatistics(params);
      
      if (response.status === 1 || response.status === 200) {
        const data = response.data as Page<OrderStatisticsData>;
        const list = data.content || [];
        
        setStatisticsList(list);
        setPagination({
          current: (data.number || 0) + 1,
          pageSize: data.size || 31,
          total: data.totalElements || 0,
        });

        // 计算汇总统计
        const totalOrders = list.reduce((sum, item) => sum + item.orderCount, 0);
        const totalPaidOrders = list.reduce((sum, item) => sum + item.paidOrderCount, 0);
        const totalAmount = list.reduce((sum, item) => sum + item.totalAmount, 0);
        const avgPaymentRate = totalOrders > 0 ? (totalPaidOrders / totalOrders * 100) : 0;

        setSummaryStats({
          totalOrders,
          totalPaidOrders,
          totalAmount,
          avgPaymentRate,
        });
      } else {
        message.error(response.msg || '获取统计数据失败');
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
      message.error('获取统计数据失败');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedDealerId, showGlobalOnly, isAdmin, pagination.pageSize]);

  // 加载实时统计数据
  const loadRealtimeStatistics = useCallback(async () => {
    setRealtimeLoading(true);
    try {
      const params: OrderStatisticsRealtimeRequest = {};

      if (showGlobalOnly && isAdmin) {
        params.globalOnly = true;
      } else if (isAdmin && !selectedDealerId) {
        params.allDealers = true;
      } else if (selectedDealerId) {
        params.dealerId = selectedDealerId;
      }

      const response = await orderService.getRealtimeStatistics(params);
      
      if (response.status === 1 || response.status === 200) {
        const data = response.data || [];
        setRealtimeData(data);
        message.success('实时统计数据已更新');
      } else {
        message.error(response.msg || '获取实时统计失败');
      }
    } catch (error) {
      console.error('获取实时统计失败:', error);
      message.error('获取实时统计失败');
    } finally {
      setRealtimeLoading(false);
    }
  }, [selectedDealerId, showGlobalOnly, isAdmin]);

  // 初始加载
  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  // 手动重算统计
  const handleRecalculate = () => {
    Modal.confirm({
      title: '确认重算统计数据',
      content: (
        <div>
          <p>确定要重新计算以下时间范围的统计数据吗？</p>
          <p><strong>开始日期：</strong>{dateRange[0].format('YYYY-MM-DD')}</p>
          <p><strong>结束日期：</strong>{dateRange[1].format('YYYY-MM-DD')}</p>
          {selectedDealerId && <p><strong>经销商ID：</strong>{selectedDealerId}</p>}
          <Alert
            message="注意"
            description="重算会覆盖已有的统计数据，建议在非业务高峰期执行"
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
          />
        </div>
      ),
      okText: '确认重算',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        setRecalculateLoading(true);
        try {
          const params: OrderStatisticsRecalculateRequest = {
            startDate: dateRange[0].format('YYYY-MM-DD'),
            endDate: dateRange[1].format('YYYY-MM-DD'),
          };
          
          if (selectedDealerId) {
            params.dealerId = selectedDealerId;
          }

          const response = await orderService.recalculateStatistics(params);
          
          if (response.status === 1 || response.status === 200) {
            message.success('统计数据重算任务已提交，请稍后刷新查看结果');
            // 3秒后自动刷新
            setTimeout(() => {
              loadStatistics(pagination.current);
            }, 3000);
          } else {
            message.error(response.msg || '提交重算任务失败');
          }
        } catch (error) {
          console.error('重算失败:', error);
          message.error('提交重算任务失败');
        } finally {
          setRecalculateLoading(false);
        }
      },
    });
  };

  // 表格列定义
  const columns = [
    {
      title: '日期',
      dataIndex: 'statDate',
      key: 'statDate',
      width: 120,
      fixed: 'left' as const,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    ...((!showGlobalOnly || !isAdmin) ? [{
      title: '经销商',
      key: 'dealer',
      width: 180,
      render: (record: OrderStatisticsData) => (
        <div>
          <div>{record.dealerName || '-'}</div>
          {record.dealerId && (
            <div style={{ fontSize: '12px', color: '#999' }}>ID: {record.dealerId}</div>
          )}
        </div>
      ),
    }] : []),
    {
      title: '订单总数',
      dataIndex: 'orderCount',
      key: 'orderCount',
      width: 120,
      render: (count: number) => count.toLocaleString(),
    },
    {
      title: '已支付订单',
      dataIndex: 'paidOrderCount',
      key: 'paidOrderCount',
      width: 120,
      render: (count: number) => (
        <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>
          {count.toLocaleString()}
        </Text>
      ),
    },
    {
      title: '支付金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 150,
      render: (amount: number) => (
        <Text style={{ color: '#1890ff', fontWeight: 'bold' }}>
          ¥{amount.toFixed(2)}
        </Text>
      ),
    },
    {
      title: '支付率',
      key: 'paymentRate',
      width: 100,
      render: (record: OrderStatisticsData) => {
        const rate = record.orderCount > 0 
          ? (record.paidOrderCount / record.orderCount * 100) 
          : 0;
        const color = rate >= 90 ? '#52c41a' : rate >= 70 ? '#faad14' : '#ff4d4f';
        return (
          <Tag color={color}>
            {rate.toFixed(2)}%
          </Tag>
        );
      },
    },
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3}>订单统计</Title>
          </Col>
          <Col>
            <Space>
              {isAdmin && (
                <>
                  <Button
                    icon={<ThunderboltOutlined />}
                    onClick={loadRealtimeStatistics}
                    loading={realtimeLoading}
                  >
                    查看今日实时
                  </Button>
                  <Button
                    icon={<CalculatorOutlined />}
                    onClick={handleRecalculate}
                    loading={recalculateLoading}
                    danger
                  >
                    手动重算
                  </Button>
                </>
              )}
              <Button
                icon={<ReloadOutlined />}
                onClick={() => loadStatistics(pagination.current)}
                loading={loading}
              >
                刷新
              </Button>
            </Space>
          </Col>
        </Row>

        {/* 查询条件 */}
        <Card size="small" style={{ marginBottom: 24, background: '#fafafa' }}>
          <Space wrap>
            <span>日期范围：</span>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              format="YYYY-MM-DD"
              disabledDate={(current) => current && current > dayjs().subtract(1, 'day').endOf('day')}
            />

            {isAdmin && (
              <>
                <span>经销商ID：</span>
                <Input
                  placeholder="留空查询所有"
                  style={{ width: 150 }}
                  value={selectedDealerId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedDealerId(value ? Number(value) : undefined);
                  }}
                  disabled={showGlobalOnly}
                />

                <span>全平台汇总：</span>
                <Switch
                  checked={showGlobalOnly}
                  onChange={(checked) => {
                    setShowGlobalOnly(checked);
                    if (checked) {
                      setSelectedDealerId(undefined);
                    }
                  }}
                  checkedChildren={<GlobalOutlined />}
                  unCheckedChildren="否"
                />
              </>
            )}

            <Button type="primary" onClick={() => loadStatistics(1)} loading={loading}>
              查询
            </Button>
          </Space>
        </Card>

        {/* 汇总统计卡片 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="订单总数"
                value={summaryStats.totalOrders}
                prefix={<ShoppingCartOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="已支付订单"
                value={summaryStats.totalPaidOrders}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="支付总金额"
                value={summaryStats.totalAmount}
                precision={2}
                prefix={<DollarOutlined />}
                suffix="元"
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="平均支付率"
                value={summaryStats.avgPaymentRate}
                precision={2}
                suffix="%"
                valueStyle={{ 
                  color: summaryStats.avgPaymentRate >= 90 ? '#52c41a' : 
                         summaryStats.avgPaymentRate >= 70 ? '#faad14' : '#ff4d4f' 
                }}
              />
            </Card>
          </Col>
        </Row>

        {/* 实时统计数据展示 */}
        {realtimeData.length > 0 && (
          <Card 
            title={
              <Space>
                <ThunderboltOutlined style={{ color: '#ff4d4f' }} />
                <span>今日实时统计（{dayjs().format('YYYY-MM-DD')}）</span>
                <Tag color="red">实时</Tag>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <Table
              columns={columns}
              dataSource={realtimeData}
              rowKey={(record) => `realtime-${record.dealerId || 'global'}-${record.statDate}`}
              pagination={false}
              size="small"
            />
          </Card>
        )}

        <Divider />

        {/* 历史统计数据表格 */}
        <Card title="历史统计数据">
          <Table
            columns={columns}
            dataSource={statisticsList}
            loading={loading}
            rowKey={(record) => `${record.dealerId || 'global'}-${record.statDate}`}
            scroll={{ x: 800 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
              pageSizeOptions: ['10', '20', '31', '50', '100'],
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, pageSize }));
                loadStatistics(page);
              },
            }}
          />
        </Card>
      </Card>
    </div>
  );
};

export default OrderStatistics;
